// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, CardMedia, Collapse } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import { B1Tiny, B3Tiny, ModalActionsLoadingButton } from "../../../theme/styled";

// ----------------------------------------------------------------------
// Help Section Configuration
// ----------------------------------------------------------------------
const TRADE_SPLITTING_FUNCTION_PRINCIPLE_HELP_IMAGE = "/illustrations/converted/trade_splittings/function-principle.png";
const TRADE_SPLITTING_FUNCTION_PRINCIPLE_HELP_DESCRIPTION = (
  <>
    Trade splitting is a strategy in trading where a single trade is divided into several smaller trades. This approach is often used to manage risk
    and enhance trade execution. For example, a trader may split a trade into two or more parts instead of investing the entire amount. Each part is
    then executed separately. This can be done in any ratio such as 50:50, 30:70, 50:30:20, 30:30:20:20, or other ratios. A tactic like that allows a
    trader to maximize profits and minimize losses by adjusting based on the market's response to the initial trade. It's a flexible strategy that can
    be particularly beneficial in volatile markets. <br />
    <br />
    The trade splitting feature allows you to split your trade in up to 25 sub-trades. Each sub-trade can have different trade entry and exit points,{" "}
    <b>with the first trade used as a reference, the so-called master trade</b>. The first trade is that one which is opened based on the actual trade
    signal. As soon as the split trade feature is enabled, the principle applies to each trade the trading module opens. <br />
    <br />
    <b>
      As standard, the initial stop loss difference of the master trade serves as the reference for each split trade. It represents the unitless value
      of 100.
    </b>{" "}
    Let's assume the initial stop loss difference is 100 pips. If the input parameter <b>Open position</b> is set to 190, the second trade will be
    opened when the price reaches the point: Initial stop loss + 190 pips. If the <b>Open position</b> is configured with 210 it will open a trade at
    initial stop loss value + 210 pips. This relative price description is also used to define the take profit value. Please note that the stop loss
    trailing will be reused from the master trade, even with the same trailing characteristic. <br />
    <br />
    Based on this relative price position description, the minimum value of the open positions can be 0, and the take profit level of the master trade
    defines the maximum value. For the <b>Close position</b>, the same minimum and maximum values apply, except 0, which means the same take profit as
    the master trade will be used, and -1, which means no take profit is used. <br />
    <br />
    <b>A split trade is only active if the volume is unequal to zero but larger than 0.</b> This input parameter is a percentage of the master trade
    volume. For example, if the master trade is open with 1.20 lots, then <b>Open volume</b> = 50, which means a trade with 0.60 lots in volume size
    will be opened when <b>Open position</b> is reached. A split trade is disabled when the volume configuration equals 0.
  </>
);

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const TradeSplittingDefaults = {
  // TSP parameters
  nOpenVolume: "0",
  nOpenPosition: "30",
  nClosePosition: "0",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  nOpenVolume: {
    min: 0,
  },
  nOpenPosition: {
    min: 0,
  },
  nClosePosition: {
    min: -1,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const TradeSplittingsBuildName = (config) => {
  const parts = [];

  parts.push(`OV: ${config.nOpenVolume}`);
  parts.push(`OP: ${config.nOpenPosition}`);
  parts.push(`CP: ${config.nClosePosition}`);

  return parts.join(" | ");
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const TradeSplittingsBuildSubtitle = (config) => {
  const parts = [];

  parts.push(`OV: ${config.nOpenVolume}`);
  parts.push(`OP: ${config.nOpenPosition}`);
  parts.push(`CP: ${config.nClosePosition}`);

  return parts.join(" | ");
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function TradeSplittingsRenderParameters(selectedIndicator, getIndicatorConfig, professionalMode) {
  const config = getIndicatorConfig(selectedIndicator);

  if (!config) {
    return (
      <Typography variant="contentMiddle" align="center">
        No configuration saved yet!
      </Typography>
    );
  }

  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Stack spacing={0.5}>
      {renderParam("Open volume", config.nOpenVolume)}
      {renderParam("Open position", config.nOpenPosition)}
      {renderParam("Close position", config.nClosePosition)}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const TradeSplittingsConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  TradeSplittingsConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");

  const autoCloseTimeoutRef = useRef(null);

  const [collapse, setCollapse] = useState(false);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Clear cross-validation error state
    setError(false);
    setReturnMessage("");

    // Apply all the default values to the form
    Object.keys(TradeSplittingDefaults).forEach((key) => {
      setFieldValue(key, TradeSplittingDefaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      // TSP parameters
      nOpenVolume: initialData?.nOpenVolume ?? TradeSplittingDefaults.nOpenVolume,
      nOpenPosition: initialData?.nOpenPosition ?? TradeSplittingDefaults.nOpenPosition,
      nClosePosition: initialData?.nClosePosition ?? TradeSplittingDefaults.nClosePosition,
    },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
          console.error("Save error:", err);
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    setErrors,
    setTouched,
    resetForm,
    setFieldError,
  } = formik;

  // ----------------------------------------------------------------------
  // Cross-validation logic
  // ----------------------------------------------------------------------
  useEffect(() => {
    const nOpenPosition = parseFloat(values.nOpenPosition);
    const nClosePosition = parseFloat(values.nClosePosition);

    if (!Number.isNaN(nOpenPosition) && !Number.isNaN(nClosePosition)) {
      if (nClosePosition > 0) {
        if (nOpenPosition <= 0 || nOpenPosition >= nClosePosition) {
          setError(true);
          setReturnMessage(
            <>
              <b>Close position</b> cannot be smaller than <b>Open position</b>!
            </>
          );
        } else {
          setError(false);
          setReturnMessage("");
        }
      } else {
        setError(false);
        setReturnMessage("");
      }
    }
  }, [values.nOpenPosition, values.nClosePosition]);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }

  // Disable save button if cross-validation fails
  if (error) {
    saveAndCloseDisabled = true;
  }

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure trade splitting
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure trade splitting</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Help Sections */}
                  {/* ---------------------------------------------------------------------- */}
                  {TRADE_SPLITTING_FUNCTION_PRINCIPLE_HELP_IMAGE && (
                    <Box>
                      <B3Tiny
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setCollapse(!collapse);
                        }}
                      >
                        {collapse ? "Hide help section" : "Show help section"}
                      </B3Tiny>

                      <Collapse sx={{ p: "3px" }} in={collapse} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1 }}>
                          <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                            {TRADE_SPLITTING_FUNCTION_PRINCIPLE_HELP_DESCRIPTION}
                            <br />
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            borderRadius: "10px",
                            py: 2,
                            boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                            backgroundColor: "white",
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={TRADE_SPLITTING_FUNCTION_PRINCIPLE_HELP_IMAGE}
                            alt="Trade splitting help image"
                            sx={{
                              objectFit: "contain",
                              maxHeight: "483px",
                            }}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nOpenVolume */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Open volume"
                      helpReference="HandleTradeSplittings"
                      helpText=""
                      helpLink=""
                      fieldName="nOpenVolume"
                      touched={touched}
                      errors={errors}
                    />
                    <SmartNumberInput textFieldName="nOpenVolume" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nOpenPosition */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Open position"
                      helpReference="HandleTradeSplittings"
                      helpText=""
                      helpLink=""
                      fieldName="nOpenPosition"
                      touched={touched}
                      errors={errors}
                    />
                    <SmartNumberInput textFieldName="nOpenPosition" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Parameter configuration */}
                  {/* nClosePosition */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <SmartParameter
                      value="Close position"
                      helpReference="HandleTradeSplittings"
                      helpText=""
                      helpLink=""
                      fieldName="nClosePosition"
                      touched={touched}
                      errors={errors}
                    />
                    <SmartNumberInput textFieldName="nClosePosition" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
